/**
 * P&L Audit Tab Component
 *
 * Provides detailed P&L reconciliation with:
 * - Spot P&L breakdown (buy/sell orders)
 * - Futures P&L breakdown (realized P&L, funding, commissions)
 * - Final P&L calculation with formula
 * - Order verification warnings
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Grid, Divider, Alert } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';

import type { StrategyInfo, Order, FuturesOrder } from '../../../types/strategy';
import { buildPnlSourceView } from '../../../utils/pnlSourceQuality';
import { traderTheme as theme } from '../traderDashboardTheme';

interface PnLAuditTabProps {
  strategy: StrategyInfo;
}

function formatUsdt(value: number | null | undefined, decimals = 2): string {
  if (value == null) return '-';
  const sign = value >= 0 ? '+' : '';
  return `${sign}$${value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

function formatUsdtNoSign(value: number | null | undefined, decimals = 2): string {
  if (value == null) return '-';
  return `$${Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

function formatPercent(value: number | null | undefined): string {
  if (value == null) return '-';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function formatQuote(value: number | null | undefined, quote: string, decimals = 2): string {
  if (value == null) return '-';
  // For KRW, use different formatting (no decimals, use comma)
  if (quote === 'KRW') {
    return `₩${Math.round(value).toLocaleString('ko-KR')}`;
  }
  return `${value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} ${quote}`;
}

interface LineItemProps {
  label: string;
  value: number;
  color?: string;
  isTotal?: boolean;
  showSign?: boolean;
  decimals?: number;
  sublabel?: string;
}

const LineItem: React.FC<LineItemProps> = ({
  label,
  value,
  color,
  isTotal = false,
  showSign = true,
  decimals = 2,
  sublabel,
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: isTotal ? 1.5 : 0.75,
      borderTop: isTotal ? `2px solid ${theme.border}` : 'none',
      mt: isTotal ? 1 : 0,
    }}
  >
    <Box>
      <Typography
        variant={isTotal ? 'body1' : 'body2'}
        sx={{
          color: theme.text.primary,
          fontWeight: isTotal ? 600 : 400,
          fontSize: isTotal ? '0.95rem' : '0.85rem',
        }}
      >
        {label}
      </Typography>
      {sublabel && (
        <Typography variant="caption" sx={{ color: theme.text.muted, fontSize: '0.7rem' }}>
          {sublabel}
        </Typography>
      )}
    </Box>
    <Typography
      variant={isTotal ? 'body1' : 'body2'}
      sx={{
        color: color || (value >= 0 ? theme.profit : theme.loss),
        fontFamily: 'monospace',
        fontWeight: isTotal ? 700 : 500,
        fontSize: isTotal ? '1rem' : '0.85rem',
      }}
    >
      {showSign ? formatUsdt(value, decimals) : formatUsdtNoSign(value, decimals)}
    </Typography>
  </Box>
);

export const PnLAuditTab: React.FC<PnLAuditTabProps> = ({ strategy }) => {
  const { t } = useTranslation();
  const ctx = strategy.execution_context;
  const pnlSnapshot = ctx.pnl_snapshot;

  // Calculate spot P&L from orders
  const spotAudit = useMemo(() => {
    const orders = Object.values(ctx.orders) as Order[];

    const buyOrders = orders.filter((o) => o.side === 'Bid' && o.executed_volume > 0);
    const sellOrders = orders.filter((o) => o.side === 'Ask' && o.executed_volume > 0);

    const totalCost = buyOrders.reduce((sum, o) => {
      const cost = o.total || o.price * o.executed_volume;
      const rate = o.quote_to_usdt_rate || 1.0;
      return sum + cost * rate;
    }, 0);

    const totalRevenue = sellOrders.reduce((sum, o) => {
      const revenue = o.total || o.price * o.executed_volume;
      const rate = o.quote_to_usdt_rate || 1.0;
      return sum + revenue * rate;
    }, 0);

    const totalFees = orders.reduce((sum, o) => {
      const fee = o.paid_fee || 0;
      const rate = o.quote_to_usdt_rate || 1.0;
      return sum + fee * rate;
    }, 0);

    const spotPnl = totalRevenue - totalCost;

    // Check for unfetched orders
    const unfetchedOrders = orders.filter((o) => !o.fetched);

    // Get quote currency from first order (they should all be the same)
    const quoteCurrency = orders[0]?.quote || 'USDT';

    // Calculate totals in original quote currency (before USDT conversion)
    const totalCostInQuote = buyOrders.reduce((sum, o) => {
      return sum + (o.total || o.price * o.executed_volume);
    }, 0);

    const totalRevenueInQuote = sellOrders.reduce((sum, o) => {
      return sum + (o.total || o.price * o.executed_volume);
    }, 0);

    return {
      buyOrders,
      sellOrders,
      totalCost,
      totalRevenue,
      totalFees,
      spotPnl,
      unfetchedOrders,
      quoteCurrency,
      totalCostInQuote,
      totalRevenueInQuote,
    };
  }, [ctx.orders]);

  // Calculate futures P&L
  const futuresAudit = useMemo(() => {
    const orders = Object.values(ctx.futures_orders) as FuturesOrder[];
    const fetched = pnlSnapshot?.fetched;

    // From orders
    // In one-way mode (position_side=Both): Ask = Open Short, Bid = Close Short
    // reduce_only flag is optional - any Bid order closes a short position
    const openOrders = orders.filter((o) => o.side === 'Ask' && o.executed_volume > 0);
    const closeOrders = orders.filter((o) => o.side === 'Bid' && o.executed_volume > 0);

    const totalOpenNotional = openOrders.reduce((sum, o) => {
      const price = o.avg_price || o.price;
      return sum + price * o.executed_volume;
    }, 0);

    const totalCloseNotional = closeOrders.reduce((sum, o) => {
      const price = o.avg_price || o.price;
      return sum + price * o.executed_volume;
    }, 0);

    // Calculated realized P&L from orders
    const calculatedRealizedPnl = totalOpenNotional - totalCloseNotional;

    // Binance values remain audit evidence until strict archive coverage verifies them.
    const fetchedRealizedPnl = fetched?.realized_pnl_sum ?? 0;
    const localHedgePnl = pnlSnapshot?.calculated_hedge_pnl
      ?? calculatedRealizedPnl + (ctx.funding_fee?.total_income ?? 0);
    const source = buildPnlSourceView(fetched, localHedgePnl, calculatedRealizedPnl);

    // Difference between calculated and fetched (for verification)
    const pnlDifference = Math.abs(calculatedRealizedPnl - fetchedRealizedPnl);

    return {
      openOrders,
      closeOrders,
      totalOpenNotional,
      totalCloseNotional,
      calculatedRealizedPnl,
      source,
      pnlDifference,
    };
  }, [ctx.futures_orders, ctx.funding_fee, pnlSnapshot]);

  // User trades from API
  const userTrades = useMemo(() => {
    const trades = pnlSnapshot?.fetched?.user_trades || [];
    return trades.sort((a, b) => b.time - a.time);
  }, [pnlSnapshot]);

  // Calculate opened/closed volume from user trades
  const userTradesVolume = useMemo(() => {
    // For futures short strategies:
    // SELL = opening short position (opened volume)
    // BUY = closing short position (closed volume)
    const openedVolume = userTrades
      .filter((t) => t.side === 'SELL')
      .reduce((sum, t) => sum + (t.qty || 0), 0);
    const closedVolume = userTrades
      .filter((t) => t.side === 'BUY')
      .reduce((sum, t) => sum + (t.qty || 0), 0);
    return { openedVolume, closedVolume };
  }, [userTrades]);

  // Final P&L calculation
  const finalPnL = useMemo(() => {
    // If we have pnl_snapshot, use it as source of truth
    if (pnlSnapshot) {
      const spotPnl = pnlSnapshot.spot_earned_usdt - pnlSnapshot.spot_spent_usdt;
      const hedgePnl = futuresAudit.source.hedgePnl;
      const netPnl = pnlSnapshot.net_pnl_usdt;
      const percent = pnlSnapshot.spot_spent_usdt > 0
        ? (netPnl / pnlSnapshot.spot_spent_usdt) * 100
        : 0;

      return {
        spotPnl,
        hedgePnl,
        netPnl,
        percent,
        hasSnapshot: true,
        spotCost: pnlSnapshot.spot_spent_usdt,
        spotRevenue: pnlSnapshot.spot_earned_usdt,
      };
    }

    // Otherwise calculate from orders
    const spotPnl = spotAudit.spotPnl;
    const hedgePnl = futuresAudit.source.hedgePnl;
    const netPnl = spotPnl + hedgePnl;
    const percent = spotAudit.totalCost > 0 ? (netPnl / spotAudit.totalCost) * 100 : 0;

    return {
      spotPnl,
      hedgePnl,
      netPnl,
      percent,
      hasSnapshot: false,
      spotCost: spotAudit.totalCost,
      spotRevenue: spotAudit.totalRevenue,
    };
  }, [pnlSnapshot, spotAudit, futuresAudit]);

  // Warnings
  const warnings = useMemo(() => {
    const list: string[] = [];

    if (spotAudit.unfetchedOrders.length > 0) {
      list.push(t('traderDashboard.strategyDetail.pnlAudit.warnings.unfetchedOrders', { count: spotAudit.unfetchedOrders.length }));
    }

    if ((futuresAudit.pnlDifference ?? 0) > 1) {
      list.push(t('traderDashboard.strategyDetail.pnlAudit.warnings.pnlMismatch', { diff: futuresAudit.pnlDifference?.toFixed(2) ?? '-' }));
    }

    if (!finalPnL.hasSnapshot) {
      list.push(t('traderDashboard.strategyDetail.pnlAudit.warnings.noSnapshot'));
    }

    // Note: Volume mismatch warning removed as purchased - sold = remaining is now calculated in the UI
    // The remaining_target_volume from backend has a different meaning (sell target - sold)

    return list;
  }, [spotAudit, futuresAudit, finalPnL, t]);

  const pnlColor = finalPnL.netPnl >= 0 ? theme.profit : theme.loss;

  return (
    <Box>
      {/* Warnings */}
      {warnings.length > 0 && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{
            mb: 3,
            backgroundColor: 'rgba(210, 153, 34, 0.1)',
            color: theme.warning,
            '& .MuiAlert-icon': { color: theme.warning },
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('traderDashboard.strategyDetail.pnlAudit.warnings.title')}
            </Typography>
            {warnings.map((warning, index) => (
              <Typography key={index} variant="caption" sx={{ display: 'block' }}>
                • {warning}
              </Typography>
            ))}
          </Box>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Spot P&L Section */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: theme.bg.secondary,
              border: `1px solid ${theme.border}`,
              height: '100%',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: theme.text.primary, fontWeight: 600, mb: 2 }}
            >
              {t('traderDashboard.strategyDetail.pnlAudit.spotSection')}
            </Typography>

            <LineItem
              label={t('traderDashboard.strategyDetail.pnlAudit.spotCost')}
              value={-finalPnL.spotCost}
              color={theme.loss}
              sublabel={`${spotAudit.buyOrders.length} ${t('traderDashboard.strategyDetail.orders.orders')} • ${formatQuote(spotAudit.totalCostInQuote, spotAudit.quoteCurrency)}`}
            />
            <LineItem
              label={t('traderDashboard.strategyDetail.pnlAudit.spotRevenue')}
              value={finalPnL.spotRevenue}
              color={theme.profit}
              sublabel={`${spotAudit.sellOrders.length} ${t('traderDashboard.strategyDetail.orders.orders')} • ${formatQuote(spotAudit.totalRevenueInQuote, spotAudit.quoteCurrency)}`}
            />
            <LineItem
              label={t('traderDashboard.strategyDetail.pnlAudit.spotFees')}
              value={-spotAudit.totalFees}
              color={theme.text.secondary}
              sublabel={t('traderDashboard.strategyDetail.pnlAudit.includedInTotal')}
              decimals={4}
            />
            <LineItem
              label={t('traderDashboard.strategyDetail.pnlAudit.spotPnl')}
              value={finalPnL.spotPnl}
              isTotal
            />
          </Paper>
        </Grid>

        {/* Futures P&L Section */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: theme.bg.secondary,
              border: `1px solid ${theme.border}`,
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: theme.text.primary, fontWeight: 600 }}>
                {t('traderDashboard.strategyDetail.pnlAudit.futuresSection')}
              </Typography>
              {pnlSnapshot && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {futuresAudit.source.isVerified ? (
                    <CheckCircleIcon sx={{ fontSize: 14, color: theme.profit }} />
                  ) : (
                    <WarningIcon sx={{ fontSize: 14, color: theme.warning }} />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      color: futuresAudit.source.isVerified ? theme.profit : theme.warning,
                      fontSize: '0.65rem',
                    }}
                  >
                    {t(`traderDashboard.strategyDetail.pnlAudit.${futuresAudit.source.labelKey}`)}
                  </Typography>
                </Box>
              )}
            </Box>

            <LineItem
              label={t('traderDashboard.strategyDetail.pnlAudit.realizedPnl')}
              value={futuresAudit.source.realizedPnl}
              sublabel={`${futuresAudit.closeOrders.length} ${t('traderDashboard.strategyDetail.futuresOrders.closeShort')}`}
            />
            <LineItem
              label={t('traderDashboard.strategyDetail.pnlAudit.fundingFees')}
              value={futuresAudit.source.fundingFees}
              decimals={4}
            />
            <LineItem
              label={t('traderDashboard.strategyDetail.pnlAudit.commissions')}
              value={-futuresAudit.source.commissions}
              color={theme.loss}
              decimals={4}
            />
            <LineItem
              label={t('traderDashboard.strategyDetail.pnlAudit.hedgePnl')}
              value={finalPnL.hedgePnl}
              isTotal
            />
            {pnlSnapshot && (
              <Typography variant="caption" sx={{ color: theme.text.muted, display: 'block', mt: 1 }}>
                {t('traderDashboard.strategyDetail.pnlAudit.coverage', {
                  covered: futuresAudit.source.coveredQuantity,
                  expected: futuresAudit.source.expectedQuantity,
                  incomplete: futuresAudit.source.incompleteOrderCount,
                })}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Final P&L */}
      <Paper
        sx={{
          mt: 3,
          p: 3,
          backgroundColor: theme.bg.secondary,
          border: `2px solid ${pnlColor}40`,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ color: theme.text.primary, fontWeight: 600, mb: 2 }}
        >
          {t('traderDashboard.strategyDetail.pnlAudit.finalPnl')}
        </Typography>

        {/* Formula */}
        <Box
          sx={{
            p: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            borderRadius: 1,
            mb: 2,
            fontFamily: 'monospace',
          }}
        >
          <Typography variant="body2" sx={{ color: theme.text.secondary, mb: 1 }}>
            {t('traderDashboard.strategyDetail.pnlAudit.formula')}:
          </Typography>
          <Typography variant="body1" sx={{ color: theme.text.primary }}>
            {t('traderDashboard.strategyDetail.pnlAudit.formulaExpression')}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.text.muted, mt: 1 }}>
            = ({formatUsdtNoSign(finalPnL.spotRevenue)} - {formatUsdtNoSign(finalPnL.spotCost)}) + ({formatUsdt(finalPnL.hedgePnl)})
          </Typography>
          <Typography variant="body2" sx={{ color: theme.text.muted }}>
            = {formatUsdt(finalPnL.spotPnl)} + {formatUsdt(finalPnL.hedgePnl)}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: theme.border, my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ color: pnlColor, fontWeight: 700, fontFamily: 'monospace' }}>
              {formatUsdt(finalPnL.netPnl)}
            </Typography>
            <Typography variant="body1" sx={{ color: pnlColor }}>
              {formatPercent(finalPnL.percent)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: theme.text.secondary, display: 'block' }}>
              {t('traderDashboard.strategyDetail.pnlAudit.investedAmount')}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.text.primary, fontFamily: 'monospace' }}>
              {formatUsdtNoSign(finalPnL.spotCost)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* User Trades (if available) */}
      {userTrades.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: theme.text.primary, fontWeight: 600 }}
            >
              {t('traderDashboard.strategyDetail.pnlAudit.userTrades')} ({userTrades.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: theme.loss, fontFamily: 'monospace' }}>
                오픈: {userTradesVolume.openedVolume.toFixed(6)}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.profit, fontFamily: 'monospace' }}>
                청산: {userTradesVolume.closedVolume.toFixed(6)}
              </Typography>
              {(Math.abs(userTradesVolume.openedVolume - futuresAudit.openOrders.reduce((s, o) => s + o.executed_volume, 0)) > 0.00000001 ||
                Math.abs(userTradesVolume.closedVolume - futuresAudit.closeOrders.reduce((s, o) => s + o.executed_volume, 0)) > 0.00000001) && (
                <WarningIcon sx={{ color: theme.warning, fontSize: 14 }} />
              )}
            </Box>
          </Box>
          <Paper
            sx={{
              backgroundColor: theme.bg.secondary,
              border: `1px solid ${theme.border}`,
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 1,
                px: 2,
                backgroundColor: theme.bg.primary,
                borderBottom: `2px solid ${theme.border}`,
              }}
            >
              <Typography variant="caption" sx={{ width: '15%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem' }}>
                {t('traderDashboard.strategyDetail.orders.time')}
              </Typography>
              <Typography variant="caption" sx={{ width: '12%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem' }}>
                {t('traderDashboard.strategyDetail.orders.side')}
              </Typography>
              <Typography variant="caption" sx={{ width: '13%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
                {t('traderDashboard.strategyDetail.orders.price')}
              </Typography>
              <Typography variant="caption" sx={{ width: '13%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
                {t('traderDashboard.strategyDetail.orders.qty')}
              </Typography>
              <Typography variant="caption" sx={{ width: '15%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
                {t('traderDashboard.strategyDetail.pnlAudit.realizedPnl')}
              </Typography>
              <Typography variant="caption" sx={{ width: '15%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
                {t('traderDashboard.strategyDetail.orders.commission')}
              </Typography>
              <Typography variant="caption" sx={{ width: '17%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
                {t('traderDashboard.strategyDetail.orders.notional')}
              </Typography>
            </Box>

            {/* Rows */}
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {userTrades.map((trade, index) => (
                <Box
                  key={`${trade.id}-${index}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1,
                    px: 2,
                    borderBottom: `1px solid ${theme.border}`,
                    '&:hover': { backgroundColor: theme.bg.hover },
                  }}
                >
                  <Typography variant="caption" sx={{ width: '15%', color: theme.text.secondary, fontSize: '0.7rem' }}>
                    {new Date(trade.time).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      width: '12%',
                      color: trade.side === 'BUY' ? theme.profit : theme.loss,
                      fontWeight: 500,
                      fontSize: '0.75rem',
                    }}
                  >
                    {trade.side}
                  </Typography>
                  <Typography variant="caption" sx={{ width: '13%', color: theme.text.primary, fontFamily: 'monospace', fontSize: '0.75rem', textAlign: 'right' }}>
                    ${trade.price != null ? trade.price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 8 }) : '-'}
                  </Typography>
                  <Typography variant="caption" sx={{ width: '13%', color: theme.text.primary, fontFamily: 'monospace', fontSize: '0.75rem', textAlign: 'right' }}>
                    {trade.qty?.toFixed(6) ?? '-'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      width: '15%',
                      color: trade.realizedPnl >= 0 ? theme.profit : theme.loss,
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      textAlign: 'right',
                    }}
                  >
                    {formatUsdt(trade.realizedPnl, 4)}
                  </Typography>
                  <Typography variant="caption" sx={{ width: '15%', color: theme.text.secondary, fontFamily: 'monospace', fontSize: '0.75rem', textAlign: 'right' }}>
                    ${trade.commission?.toFixed(4) ?? '-'}
                  </Typography>
                  <Typography variant="caption" sx={{ width: '17%', color: theme.text.primary, fontFamily: 'monospace', fontSize: '0.75rem', textAlign: 'right' }}>
                    ${trade.quoteQty != null ? trade.quoteQty.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 8 }) : '-'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Info */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          backgroundColor: 'rgba(88, 166, 255, 0.1)',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <InfoIcon sx={{ color: theme.accent, fontSize: 20, mt: 0.25 }} />
        <Box>
          <Typography variant="body2" sx={{ color: theme.accent, fontWeight: 500 }}>
            {t('traderDashboard.strategyDetail.pnlAudit.infoTitle')}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.text.secondary }}>
            {t('traderDashboard.strategyDetail.pnlAudit.infoDescription')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PnLAuditTab;
